import { DeleteOutlined, EditOutlined, PlusOutlined, SafetyOutlined } from "@ant-design/icons";
import { Button, Input, Popconfirm, Space, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/shared/components/DataTable";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { AssignRoleModal } from "../components/AssignRoleModal";
import { UserFormModal } from "../components/UserFormModal";
import {
	useAssignRole,
	useCreateUser,
	useDeleteUser,
	useRevokeRole,
	useUpdateUser,
	useUsersList,
} from "../hooks/useUsers";
import type { CreateUserInput, ManagedUser, ManagedUserListItem } from "../types";

const { Title } = Typography;

export function UsersPage() {
	const { t } = useTranslation("users");
	const [phoneFilter, setPhoneFilter] = useState("");
	const [formOpen, setFormOpen] = useState(false);
	const [editing, setEditing] = useState<ManagedUser | null>(null);
	const [rolesTarget, setRolesTarget] = useState<ManagedUser | null>(null);

	const query = useUsersList({ phone: phoneFilter || undefined });

	const createUser = useCreateUser();
	const updateUser = useUpdateUser();
	const deleteUser = useDeleteUser();
	const assignRole = useAssignRole();
	const revokeRole = useRevokeRole();

	const closeForm = () => {
		setFormOpen(false);
		setEditing(null);
	};

	const handleSubmit = (values: CreateUserInput) => {
		if (editing) {
			// Bo'sh parol yuborilmaydi — aks holda backend uni o'zgartirmoqchi deb tushunadi.
			const input = values.password ? values : { phone: values.phone };
			updateUser.mutate({ id: editing.id, input }, { onSuccess: closeForm });
		} else {
			createUser.mutate(values, { onSuccess: closeForm });
		}
	};

	const columns: ColumnsType<ManagedUserListItem> = [
		{
			title: t("list.phoneColumn"),
			dataIndex: "phone",
			key: "phone",
		},
		{
			// Hisob kimga tegishli ekanini ko'rsatadi. `null` — xodim kartasi
			// yo'q (sof administrator); munosabat bir tomonlama majburiy.
			title: t("list.employeeColumn"),
			dataIndex: "fullName",
			key: "fullName",
			render: (value: string | null) => value ?? <span className="text-gray-400">—</span>,
		},
		{
			title: t("list.rolesColumn"),
			key: "roles",
			render: (_, user) =>
				user.roles.length ? (
					<Space wrap>
						{user.roles.map((assignment) => (
							<Tag key={assignment.id}>{assignment.role?.name ?? assignment.roleId}</Tag>
						))}
					</Space>
				) : (
					<span className="text-gray-400">—</span>
				),
		},
		{
			title: t("list.createdAtColumn"),
			dataIndex: "createdAt",
			key: "createdAt",
			render: (value: string) => new Date(value).toLocaleDateString("uz-UZ"),
		},
		{
			title: t("list.actionsColumn"),
			key: "actions",
			align: "right",
			render: (_, user) => (
				<Space>
					<PermissionGate resource="roles" action="create">
						<Button
							type="text"
							icon={<SafetyOutlined />}
							onClick={() => setRolesTarget(user)}
							title={t("list.rolesAction")}
						/>
					</PermissionGate>
					<PermissionGate resource="users" action="update">
						<Button
							type="text"
							icon={<EditOutlined />}
							onClick={() => {
								setEditing(user);
								setFormOpen(true);
							}}
							title={t("list.edit")}
						/>
					</PermissionGate>
					<PermissionGate resource="users" action="delete">
						<Popconfirm
							title={t("list.deleteConfirmTitle")}
							description={t("list.deleteConfirmDescription")}
							okText={t("list.delete")}
							cancelText={t("list.cancel")}
							okButtonProps={{ danger: true }}
							onConfirm={() => deleteUser.mutate(user.id)}
						>
							<Button type="text" danger icon={<DeleteOutlined />} title={t("list.delete")} />
						</Popconfirm>
					</PermissionGate>
				</Space>
			),
		},
	];

	return (
		<div>
			<div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
				<Title level={3} className="!mb-0">
					{t("list.title")}
				</Title>
				<Space>
					<Input.Search
						placeholder={t("list.searchPlaceholder")}
						allowClear
						onSearch={(value) => {
							setPhoneFilter(value);
							query.setPage(1);
						}}
						style={{ width: 240 }}
					/>
					<PermissionGate resource="users" action="create">
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => {
								setEditing(null);
								setFormOpen(true);
							}}
						>
							{t("list.create")}
						</Button>
					</PermissionGate>
				</Space>
			</div>

			<DataTable<ManagedUserListItem> columns={columns} query={query} />

			<UserFormModal
				open={formOpen}
				editing={editing}
				isSubmitting={createUser.isPending || updateUser.isPending}
				onCancel={closeForm}
				onSubmit={handleSubmit}
			/>

			<AssignRoleModal
				open={rolesTarget !== null}
				user={rolesTarget}
				isSubmitting={assignRole.isPending || revokeRole.isPending}
				onCancel={() => setRolesTarget(null)}
				onAssign={({ roleId, scope }) => {
					if (rolesTarget) {
						assignRole.mutate(
							{ subjectId: rolesTarget.id, roleId, scope },
							{ onSuccess: () => setRolesTarget(null) }
						);
					}
				}}
				onRevoke={(roleId) => {
					if (rolesTarget) {
						revokeRole.mutate(
							{ subjectId: rolesTarget.id, roleId },
							{ onSuccess: () => setRolesTarget(null) }
						);
					}
				}}
			/>
		</div>
	);
}
